import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, CreditCard, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";
import { PlanLadder } from "@/components/PlanLadder";

export default function Billing() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [plan, setPlan] = useState("monthly");

  const load = useCallback(async () => {
    const [status, config] = await Promise.all([
      api.get("/billing/status"),
      api.get("/payments/config"),
    ]);
    setS(status.data);
    setCfg(config.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="p-10 text-neutral-500">Loading billing…</div>;

  const isAdmin = user && user !== false && user.role === "admin";
  const cycleLabel = s.status === "trialing" ? "Trial ends" : "Next billing";
  const cycleDate = s.status === "trialing" ? s.trial_ends_at : s.current_period_end;
  const annualFee = cfg?.annual_fee_usd ?? 1000;
  const payAmount = plan === "annual" ? annualFee : (s.amount_due_usd > 0 ? s.amount_due_usd : s.monthly_fee_usd);
  const ngnLabel = cfg && cfg.currency !== "USD" && payAmount > 0
    ? `${cfg.currency} ${(payAmount * cfg.usd_to_ngn_rate).toLocaleString()}`
    : null;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Billing</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Access plans</h1>
      <p className="text-neutral-400 mt-2 text-sm">
        Signing up, showing your first <span className="text-white font-mono">{s.starter_free_listings ?? 5}</span> products and your first live session are free.
        The seller plan — <span className="text-white font-mono">${s.monthly_fee_usd.toFixed(0)}</span>/month or{" "}
        <span className="text-white font-mono">${annualFee.toFixed(0)}</span>/year — switches on Buy now, unlimited products and unlimited lives.
        <span className="block mt-1 text-neutral-500">Founding sellers (first 20): first month free with unlimited listings.</span>
      </p>

      {isAdmin ? (
        <div className="mt-10 rounded-xl border border-[#34C759]/40 bg-[#34C759]/10 p-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-[#34C759]" />
            <div>
              <div className="font-bold">Admin accounts are exempt.</div>
              <div className="text-sm text-neutral-400">You have full access without billing.</div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <StatusCard state={s} cycleLabel={cycleLabel} cycleDate={cycleDate} />
            <SummaryCard state={s} ngnLabel={ngnLabel} payAmount={payAmount} />
          </div>

          <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
              {s.is_active ? "Renew now" : "Pay to unlock"}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button data-testid="plan-monthly-btn" onClick={() => setPlan("monthly")}
                className={`rounded-xl border p-4 text-left transition-colors ${plan === "monthly" ? "border-[#007AFF] bg-[#007AFF]/10" : "border-[#27272A] hover:border-neutral-600"}`}>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Monthly</div>
                <div className="mt-1 font-mono font-bold text-2xl text-white">${s.monthly_fee_usd.toFixed(0)}<span className="text-sm text-neutral-500">/mo</span></div>
                <div className="text-[11px] text-neutral-500 mt-1">Extends access by 30 days</div>
              </button>
              <button data-testid="plan-annual-btn" onClick={() => setPlan("annual")}
                className={`relative rounded-xl border p-4 text-left transition-colors ${plan === "annual" ? "border-emerald-500 bg-emerald-500/10" : "border-[#27272A] hover:border-neutral-600"}`}>
                <span className="absolute -top-2 right-3 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-black">Save ${(s.monthly_fee_usd * 12 - annualFee).toFixed(0)}</span>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Annual</div>
                <div className="mt-1 font-mono font-bold text-2xl text-white">${annualFee.toFixed(0)}<span className="text-sm text-neutral-500">/yr</span></div>
                <div className="text-[11px] text-neutral-500 mt-1">Extends access by 365 days — 2 months free</div>
              </button>
            </div>
            <PaymentMethodPicker
              purpose="subscription"
              amountUsd={payAmount}
              ngnLabel={ngnLabel}
              extraBody={{ plan }}
            />
            <p className="mt-3 text-xs text-neutral-500" data-testid="billing-rate-note">
              Nigerian cards &amp; bank transfer pay in <span className="font-mono">{cfg?.currency || "NGN"}</span> at
              <span className="font-mono"> 1 USD = ₦{cfg?.usd_to_ngn_rate ? Math.round(cfg.usd_to_ngn_rate).toLocaleString() : "—"}</span> (today&apos;s rate)
              {cfg?.flutterwave_currency === "USD" && <> · international cards pay exactly <span className="font-mono">${payAmount.toFixed(0)}</span></>}.
            </p>
          </div>

          <PlanLadder s={s} />

          <div className="mt-6 text-xs text-neutral-500">
            <Link to="/discover" className="underline hover:text-white">Back to Discover</Link>
          </div>
        </>
      )}
    </div>
  );
}

function StatusCard({ state, cycleLabel, cycleDate }) {
  const badgeColor =
    state.status === "active" ? "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10" :
    state.status === "trialing" ? "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10" :
    "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10";

  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Current status</div>
        <Clock className="h-4 w-4 text-neutral-500" />
      </div>
      <div className={`mt-3 inline-flex px-2 py-1 rounded-full text-[10px] uppercase tracking-widest border ${badgeColor}`}>
        {state.status.replace("_", " ")}
      </div>
      {cycleDate && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500">{cycleLabel}</div>
          <div className="mt-1 font-mono tabular-nums text-lg">
            {new Date(cycleDate).toLocaleString()}
          </div>
          {state.grace_days_left != null && (
            <div className="text-xs text-neutral-500 mt-1">{state.grace_days_left} day{state.grace_days_left === 1 ? "" : "s"} remaining</div>
          )}
        </div>
      )}
      {state.last_payment_at && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Last payment</div>
          <div className="mt-1 font-mono tabular-nums text-sm text-neutral-300">
            {new Date(state.last_payment_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ state, ngnLabel, payAmount }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Amount due today</div>
        <CreditCard className="h-4 w-4 text-neutral-500" />
      </div>
      <div className="mt-3 font-mono tabular-nums font-bold text-4xl">
        ${(payAmount ?? state.amount_due_usd).toFixed(2)}
      </div>
      {ngnLabel && (
        <div className="text-xs text-neutral-500 font-mono mt-1">
          ~ {ngnLabel} in Naira
        </div>
      )}
      <div className="text-xs text-neutral-500 mt-2">
        {state.is_active
          ? "You're paid up. Renew anytime to extend your access."
          : "Pay to switch on Buy now, unlimited products and unlimited lives."}
      </div>
    </div>
  );
}
